import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  ShieldCheck,
  Trash2,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";
import UsersList from "./UsersList";
import { apiFetch } from "../utils/apiClient";

const getInitials = (name = "") =>
  name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

function AdministratorsManager({ currentUser }) {
  const [administrators, setAdministrators] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchKey, setUserSearchKey] = useState(0);
  const [administratorToRemove, setAdministratorToRemove] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadAdministrators = async () => {
      setLoading(true);
      setMessage(null);

      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/administrators`,
          { signal: controller.signal },
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error || "Impossible de charger les administrateurs.",
          );
        }

        setAdministrators(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          setMessage({ type: "error", text: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadAdministrators();
    return () => controller.abort();
  }, []);

  const addAdministrator = async (event) => {
    event.preventDefault();
    if (!selectedUser || saving) return;

    if (
      administrators.some(
        (administrator) => administrator.userId === selectedUser.id,
      )
    ) {
      setMessage({
        type: "error",
        text: "Cette personne est déjà administratrice.",
      });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/administrators`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: selectedUser.id,
            email: selectedUser.mail || selectedUser.userPrincipalName || "",
            nom: selectedUser.displayName,
          }),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Impossible d'ajouter cet administrateur.",
        );
      }

      setAdministrators((current) =>
        [...current, data.administrator].sort((first, second) =>
          String(first.nom || "").localeCompare(
            String(second.nom || ""),
            "fr-CA",
          ),
        ),
      );
      setSelectedUser(null);
      setUserSearchKey((value) => value + 1);
      setMessage({
        type: "success",
        text: "Administrateur ajouté avec succès.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const removeAdministrator = async () => {
    if (!administratorToRemove || removing) return;

    setRemoving(true);
    setMessage(null);

    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/administrators/${encodeURIComponent(
          administratorToRemove.userId,
        )}`,
        { method: "DELETE" },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Impossible de retirer cet administrateur.",
        );
      }

      setAdministrators((current) =>
        current.filter(
          (administrator) =>
            administrator.userId !== administratorToRemove.userId,
        ),
      );
      setAdministratorToRemove(null);
      setMessage({
        type: "success",
        text: "Administrateur retiré avec succès.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setRemoving(false);
    }
  };

  const currentUserId = currentUser?.id;

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-200 px-4 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 sm:text-lg">
              Gestion des administrateurs
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Les administrateurs ont accès à tous les formulaires et aux outils
              de configuration.
            </p>
          </div>
        </div>
      </header>

      <div className="space-y-6 p-4 sm:p-6">
        {message && (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              message.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
            role={message.type === "error" ? "alert" : "status"}
          >
            {message.text}
          </div>
        )}

        <form
          onSubmit={addAdministrator}
          className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5"
        >
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Ajouter un administrateur
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Recherchez une personne dans Microsoft, puis confirmez son ajout.
            </p>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="min-w-0">
              <label className="mb-1.5 block text-sm font-semibold text-slate-800">
                Utilisateur
              </label>
              <UsersList
                key={userSearchKey}
                onUserSelect={setSelectedUser}
                initialValue={
                  selectedUser?.mail || selectedUser?.userPrincipalName || ""
                }
                placeholder="Rechercher par nom ou courriel"
                inputClassName="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <button
              type="submit"
              disabled={!selectedUser || saving}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
            >
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              {saving ? "Ajout..." : "Ajouter"}
            </button>
          </div>
        </form>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex items-start gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
            <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-slate-100 text-slate-700">
              <UsersRound className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Administrateurs actuels
              </h3>
              <p className="mt-0.5 text-sm text-slate-500">
                {administrators.length} administrateur
                {administrators.length > 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              Chargement des administrateurs...
            </div>
          ) : administrators.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-sm font-semibold text-slate-800">
                Aucun administrateur trouvé
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {administrators.map((administrator) => {
                const isCurrentUser = administrator.userId === currentUserId;
                const isLastAdministrator = administrators.length <= 1;
                const removalDisabled = isCurrentUser || isLastAdministrator;
                const removalReason = isCurrentUser
                  ? "Vous ne pouvez pas retirer votre propre accès administrateur."
                  : isLastAdministrator
                    ? "Le dernier administrateur ne peut pas être retiré."
                    : "Retirer cet administrateur";

                return (
                  <div
                    key={administrator.userId}
                    className="flex items-start gap-3 px-4 py-4 transition-colors hover:bg-slate-50 sm:items-center sm:px-5"
                  >
                    <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                      {getInitials(administrator.nom)}
                    </span>
                    <div className="min-w-0 flex-1 sm:grid sm:grid-cols-[minmax(10rem,0.8fr)_minmax(12rem,1.2fr)] sm:items-center sm:gap-4">
                      <p className="break-words text-sm font-semibold text-slate-900">
                        {administrator.nom || "Nom non disponible"}
                        {isCurrentUser && (
                          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            Vous
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 break-all text-xs text-slate-600 sm:mt-0 sm:text-sm">
                        {administrator.email || "Courriel non disponible"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdministratorToRemove(administrator)}
                      disabled={removalDisabled}
                      className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      aria-label={removalReason}
                      title={removalReason}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {administratorToRemove && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/55 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !removing) {
              setAdministratorToRemove(null);
            }
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="remove-administrator-title"
            className="w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl"
          >
            <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-6">
              <div className="flex min-w-0 items-start gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-red-100 text-red-700">
                  <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3
                    id="remove-administrator-title"
                    className="text-base font-semibold text-slate-900"
                  >
                    Retirer cet administrateur?
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-slate-600">
                    <strong>{administratorToRemove.nom}</strong> perdra l'accès
                    complet aux outils d'administration.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAdministratorToRemove(null)}
                disabled={removing}
                className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-50"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>
            <footer className="flex flex-col-reverse gap-2 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={() => setAdministratorToRemove(null)}
                disabled={removing}
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={removeAdministrator}
                disabled={removing}
                className="inline-flex min-h-11 items-center justify-center rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:cursor-wait disabled:opacity-60"
              >
                {removing ? "Suppression..." : "Retirer"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}

export default AdministratorsManager;
